// Graph 独立页入口 / Standalone graph page entry.
// 路由 /graph/{sim_id}（uuid）。进入即渲染骨架 + Spinner（AGENTS.md 载入约定），
// 数据返回后挂载 mountGraphExplorer；404 区分「未生成」与「不存在」空态。
// Route /graph/{sim_id}. Paints the skeleton+spinner synchronously, then
// mounts the explorer; 404s split into not-generated vs not-found states.

import { graphApi as realGraphApi } from "@/api/graph";
import { ApiError, AuthError } from "@/api/errors";
import { escapeHtml, setHidden } from "@/lib/dom";
import { log } from "@/lib/logging";
import { createLoadingBlock } from "@/lib/spinner";

type GraphApiShape = Pick<typeof realGraphApi, "get">;

const GRAPH_PATH_RE = /^\/graph\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/i;

/**
 * 判断当前 URL 是否是 graph 路由 / Whether the URL targets the graph viewer.
 */
export function isGraphPath(): boolean {
  return GRAPH_PATH_RE.test(window.location.pathname);
}

/**
 * 进入 Graph 查看模式 / Enter the standalone graph-viewer mode.
 */
export function enterGraphViewerMode(opts: { graphApi?: GraphApiShape } = {}): void {
  setHidden("console-root", true);
  setHidden("report-root", true);
  setHidden("graph-root", false);
  const simId = window.location.pathname.match(GRAPH_PATH_RE)?.[1] ?? "";
  void loadGraphViewer(simId, opts);
}

/**
 * 拉取并渲染 graph / Fetch the graph payload and mount the explorer.
 */
export async function loadGraphViewer(
  simId: string,
  opts: { graphApi?: GraphApiShape } = {},
): Promise<void> {
  const graphApi = opts.graphApi ?? realGraphApi;
  const stage = document.getElementById("graph-stage");
  const meta = document.getElementById("graph-meta");
  if (!stage) return;

  // 同步先画骨架 + Spinner / Paint the skeleton synchronously first.
  stage.replaceChildren(createLoadingBlock("Loading graph…"));
  if (meta) meta.textContent = simId;

  try {
    const payload = await graphApi.get(simId);
    const { mountGraphExplorer } = await import("@/features/graph-viewer/view");
    stage.replaceChildren();
    await mountGraphExplorer(stage, payload);
    if (meta) {
      const skill = payload.meta?.skill ? ` · ${payload.meta.skill}` : "";
      meta.textContent = `${simId}${skill}`;
    }
  } catch (error) {
    renderGraphErrorInto(stage, error);
    log.warn("graph load failed", { simId, message: String(error) });
  }
}

/**
 * 渲染错误/空态 / Render error and empty states.
 * 导出供报告页 Graph pane 复用同一套空态文案（Task 7）。
 * Exported so the report-viewer graph pane reuses the same states.
 */
export function renderGraphErrorInto(stage: HTMLElement, error: unknown): void {
  const empty = document.createElement("div");
  empty.className = "graph-explorer-empty";
  if (error instanceof ApiError && error.code === "GRAPH_NOT_GENERATED") {
    empty.innerHTML = `
      <div>
        <p>Graph data has not been generated for this simulation.</p>
        <p>Use "Regenerate graph" in Task Detail's Simulation History to generate it.</p>
      </div>
    `;
  } else if (error instanceof ApiError && error.status === 404) {
    empty.innerHTML = `<div><p>Simulation not found.</p></div>`;
  } else if (error instanceof AuthError) {
    empty.innerHTML = `<div><p>Sign in to view this graph.</p></div>`;
  } else {
    const message = error instanceof Error ? error.message : String(error);
    empty.innerHTML = `
      <div>
        <p>Graph failed to load: ${escapeHtml(message)}</p>
        <button class="button ghost graph-retry" type="button">Retry</button>
      </div>
    `;
    empty.querySelector(".graph-retry")?.addEventListener("click", () => {
      window.location.reload();
    });
  }
  stage.replaceChildren(empty);
}
